/**
 * AuthShell, centered card on muted background, used by every unauthenticated
 * page (SignIn, SignUp, ForgotPassword, ResetPassword, VerifyEmail, AcceptInvite).
 *
 * Pattern (per UX-DESIGN.md § Auth + onboarding):
 *   - Muted background
 *   - Olytic wordmark above the card
 *   - Card centered, max-width ~420px, generous padding
 *   - Footer text below card for "Don't have an account?" type links
 *
 * Pages compose their own content inside; AuthShell only owns the chrome.
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

interface AuthShellProps {
  /** Card title (e.g., "Sign in" or "Create your account") */
  title: string;
  /** Optional subtitle under the title */
  subtitle?: string;
  /** Optional eyebrow above the title (e.g., "Invited to Acme") */
  eyebrow?: string;
  /** Card content, typically a form */
  children: ReactNode;
  /** Optional footer rendered below the card (e.g., sign-in/sign-up swap link) */
  footer?: ReactNode;
}

export function AuthShell({ title, subtitle, eyebrow, children, footer }: AuthShellProps) {
  return (
    <div className="auth-shell">
      {/* Olytic brand (2026-09 refresh — matches olytic.com): planetary
         mark from /olytic-mark.svg + the OLYTIC wordmark with a brass "Y".
         (Mark-next-to-wordmark lockup introduced in Push #84 round 19,
         Ean dogfood 2026-06-22.) Auth pages render pre-session on a fixed
         light background, so the mark file is the static ink variant and
         the wordmark colors are baked (no per-workspace override here). */}
      <Link
        to="/"
        className="auth-brand"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}
      >
        <img
          src="/olytic-mark.svg"
          alt=""
          aria-hidden="true"
          width={28}
          height={28}
          style={{ display: 'block' }}
        />
        <span
          style={{
            fontFamily: "'DM Sans', Inter, system-ui, -apple-system, sans-serif",
            fontWeight: 700,
            fontSize: 20,
            letterSpacing: '0.04em',
            color: '#0F1720',
          }}
        >
          OL<span style={{ color: '#B89149' }}>Y</span>TIC
        </span>
      </Link>
      <div className="auth-card">
        {eyebrow !== undefined && (
          <div
            style={{
              color: 'var(--olytic-teal)',
              fontSize: '12px',
              fontWeight: 600,
              letterSpacing: '0.02em',
              marginBottom: '8px',
            }}
          >
            {eyebrow}
          </div>
        )}
        <h1 className="auth-title">{title}</h1>
        {subtitle !== undefined && <p className="auth-subtitle">{subtitle}</p>}
        {children}
      </div>
      {footer !== undefined && <div className="auth-card-footer">{footer}</div>}
    </div>
  );
}
